import { useState } from "react";
import { Link } from "react-router-dom";
import { PackageSearch, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";

const REASONS = ["Changed my mind", "Arrived damaged", "Wrong item received", "Doesn't match description", "Quality not as expected"];

export default function ReturnsPage() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [existingReturns, setExistingReturns] = useState([]);
  const [selected, setSelected] = useState({});
  const [reason, setReason] = useState(REASONS[0]);
  const [condition, setCondition] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const lookup = async () => {
    if (!orderNumber || !email) return toast.error("Enter your order number and email");
    setLoading(true);
    try {
      const { data } = await api.post("/orders/lookup", { order_number: orderNumber, email });
      setOrder(data.order);
      const r = await api.get(`/returns/by-email/${encodeURIComponent(email)}`);
      setExistingReturns(r.data.returns.filter((x) => x.order_number === data.order.order_number));
    } catch (e) {
      setOrder(null);
      toast.error(formatApiError(e, "Order not found"));
    }
    setLoading(false);
  };

  const eligible = order?.status === "delivered" && order?.delivered_at &&
    Date.now() - new Date(order.delivered_at).getTime() < 30 * 86400 * 1000;

  const submit = async () => {
    const items = Object.entries(selected)
      .filter(([, qty]) => qty > 0)
      .map(([product_id, qty]) => ({ product_id, quantity: qty }));
    if (!items.length) return toast.error("Select at least one item to return");
    if (!condition) return toast.error("Confirm items are unused and in original packaging");
    setSubmitting(true);
    try {
      const { data } = await api.post("/returns", {
        order_number: orderNumber,
        email,
        items,
        reason,
        condition_confirmed: condition,
        origin_url: window.location.origin,
      });
      toast.success(`Return ${data.rma_id} created — redirecting to label payment`);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-8" data-testid="returns-page">
      <p className="eyebrow mb-2">Returns center</p>
      <h1 className="font-heading text-3xl font-bold text-white">Request a return</h1>
      <div className="mt-4 grid gap-3 text-xs text-slate-400 sm:grid-cols-3">
        <div className="card-surface flex items-center gap-2 p-3"><RotateCcw size={15} className="text-amber-400" /> 30-day window from delivery</div>
        <div className="card-surface flex items-center gap-2 p-3"><ShieldCheck size={15} className="text-amber-400" /> Unused, original packaging</div>
        <div className="card-surface flex items-center gap-2 p-3"><PackageSearch size={15} className="text-amber-400" /> Label $7.99 · refund after inspection</div>
      </div>

      <div className="card-surface mt-6 flex flex-col gap-3 p-5 sm:flex-row">
        <input data-testid="returns-order-input" className="input-dark" placeholder="Order number (e.g. TD-100001)" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} />
        <input data-testid="returns-email-input" className="input-dark" type="email" placeholder="Checkout email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="returns-lookup-btn" onClick={lookup} disabled={loading} className="btn-gold shrink-0 !px-5 !py-2.5">
          {loading ? "Looking up…" : "Find order"}
        </button>
      </div>

      {order && !eligible && (
        <div className="mt-6 rounded-xl border border-rose-400/30 bg-rose-400/10 p-4 text-sm text-rose-300" data-testid="returns-not-eligible">
          This order isn't eligible for a return — orders can be returned within 30 days of delivery.
          Current status: <StatusBadge status={order.status} />.
        </div>
      )}

      {order && eligible && (
        <div className="card-surface mt-6 p-6" data-testid="returns-eligible-panel">
          <p className="font-heading font-semibold text-white">Select items to return</p>
          <div className="mt-4 space-y-3">
            {order.items.map((it) => {
              const disabled = !it.returnable;
              return (
                <label
                  key={it.product_id}
                  data-testid={`return-item-${it.product_id}`}
                  className={`flex items-center gap-4 rounded-xl border p-3 transition-colors ${
                    disabled ? "cursor-not-allowed border-white/5 opacity-50" : selected[it.product_id] ? "border-amber-400/50 bg-amber-400/5" : "border-white/10 hover:border-white/25"
                  }`}
                >
                  <input
                    type="checkbox"
                    data-testid={`return-checkbox-${it.product_id}`}
                    disabled={disabled}
                    checked={!!selected[it.product_id]}
                    onChange={(e) => setSelected((s) => ({ ...s, [it.product_id]: e.target.checked ? it.qty : 0 }))}
                    className="h-4 w-4 accent-amber-400"
                  />
                  <img src={it.image} alt={it.name} className="h-12 w-12 rounded-lg object-cover" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{it.name} × {it.qty}</p>
                    {disabled && <p className="text-xs text-rose-400">Non-returnable — digital good / final sale</p>}
                  </div>
                  <p className="text-sm font-semibold text-white">{fmt(it.price * it.qty)}</p>
                </label>
              );
            })}
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <select data-testid="return-reason-select" value={reason} onChange={(e) => setReason(e.target.value)} className="input-dark">
              {REASONS.map((r) => <option key={r} className="bg-[#121216]">{r}</option>)}
            </select>
            <label className="flex items-center gap-2 rounded-xl border border-white/10 px-4 text-xs text-slate-300">
              <input data-testid="return-condition-checkbox" type="checkbox" checked={condition} onChange={(e) => setCondition(e.target.checked)} className="h-4 w-4 accent-amber-400" />
              Items are unused & in original packaging
            </label>
          </div>
          <button data-testid="return-submit-btn" onClick={submit} disabled={submitting} className="btn-gold mt-5 w-full disabled:opacity-60">
            {submitting ? "Creating return…" : "Continue — pay $7.99 return label"}
          </button>
          <p className="mt-3 text-center text-[11px] text-slate-600">
            The return shipping label is charged now; your refund is issued to the original payment method after we receive & inspect the item.
          </p>
        </div>
      )}

      {existingReturns.length > 0 && (
        <div className="mt-8" data-testid="existing-returns">
          <h2 className="mb-4 font-heading text-xl font-semibold text-white">Your returns on this order</h2>
          <div className="space-y-3">
            {existingReturns.map((r) => (
              <div key={r.id} className="card-surface flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`return-row-${r.id}`}>
                <div>
                  <p className="font-mono font-semibold text-amber-400">{r.id}</p>
                  <p className="text-xs text-slate-500">{r.items.map((i) => i.name).join(", ")}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-white">{fmt(r.refund_amount)}</span>
                  <StatusBadge status={r.status} testid={`return-status-${r.id}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="mt-8 text-center text-xs text-slate-600">
        Questions? Ask the chat assistant — bottom right. <Link to="/track" className="text-amber-400">Track an order</Link>
      </p>
    </div>
  );
}
